import { AxiosPromise } from "axios";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import ValidatorContext from "formvalidation/FormContext";
import { useValidators } from "formvalidation/hooks";
import React, { useState } from "react";
import { Button, Message } from "semantic-ui-react";
import { SectionTemplate } from "./SectionTemplate";
import { EditNewSection } from "./types";

type EditSectionWithRedirect = {
  redirectUrl: (itemId: number) => string
}

type EditSectionWithOnSuccess = {
  onSuccess: (itemId: number) => void
}

type EditSectionTemplateProps = {
  submit: () => string | AxiosPromise<server.dto.FileUploadResult | server.dto.ApiResponse | server.dto.ApiResponseWrapper<any>>;
  sections: Array<EditNewSection>;
  noPanels?: boolean;
} & (EditSectionWithRedirect | EditSectionWithOnSuccess)

export const EditSectionTemplate = (props: EditSectionTemplateProps) => {
  const { submit, sections, noPanels } = props;
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const validators = useValidators();

  const onSubmit = async () => {
    if (isLoading) return;

    setIsLoading(true);
    const validatorResult = await validators.validateAll();

    if (validatorResult.hasErrors) {
      if (!noPanels) {
        validatorResult.scrollToFirstError();
      }
      setIsLoading(false);
      return;
    }

    const response = await submit();

    // Simple string means error message
    if (typeof response === "string") {
      setServerErrors([response]);
      return;
    }

    setServerErrors(response.data.errors.map(e => e.message));

    let itemId: number = undefined;
    let isSuccessful = false

    // We have server.dto.FileUploadResult, in this case we don't redirect to detail page
    if ("fileId" in response.data) {
      if (response.data.errors.length === 0) {
        isSuccessful = true;
      }
    }
    else if (!response.data.hasErrors) {
      isSuccessful = true;
      if ("data" in response.data && typeof response.data.data === "number") {
        itemId = response.data.data;
      }
    }

    if (isSuccessful) {
      if ("redirectUrl" in props) {
        window.location.href = props.redirectUrl(itemId);
      } else if ("onSuccess" in props) {
        props.onSuccess(itemId);
      }
    } else {
      setIsLoading(false);
    }
  }

  const mappedSections = sections.map((section) => {
    if (section.type === "custom") {
      return section;
    }

    return {
      ...section,
      fields: section.fields.map((field) => ({
        ...field,
        isEmpty: false,
        render: field.renderEditNew,
      })),
    }
  });

  const Footer = () => <div className="flex-column gap">
    {serverErrors.length > 0 && <Message color="red">
      {serverErrors.map((error) => <div key={error}>{error}</div>)}
    </Message>}
    <div className="flex">
      <div className="grow1" />
      <Button
        primary
        type="submit"
        loading={isLoading}
        disabled={isLoading}
      >
        {RESX.Buttons.btnSave}
      </Button>
    </div>
  </div>

  return <ValidatorContext settings={validators.validatorSettings}>
    <form onSubmit={(e) => {
      e.preventDefault();

      // Stop propagation because we can have a form in a form (e.g. form > modal > form)
      e.stopPropagation();
      onSubmit();
    }}>
      <SectionTemplate
        isInPageEditable={false}
        noPanels={props.noPanels}
        compact
        sections={[...mappedSections, {
          type: "custom",
          key: "submit",
          render: () =>
            props.noPanels
              ? <Footer key="saveButton" />
              : <SectionPanel.Panel key="saveButton">
                <Footer />
              </SectionPanel.Panel>
        }]}
      />
    </form>
  </ValidatorContext>
}